import { useSyncExternalStore } from 'react';
import {
  loadProgress,
  recordAttempt,
  recordBonus,
  recordMission,
  recordRevealed,
  saveProgress,
  type MissionResult,
  type Progress,
  type Stars,
} from './progress';

/** The device's level progress, shared by the elevator and the missions, saved on every change. */
let progress: Progress = loadProgress();
const listeners = new Set<() => void>();

function set(next: Progress) {
  if (next === progress) return;
  progress = next;
  saveProgress(progress);
  for (const l of listeners) l();
}

export const progressStore = {
  get: (): Progress => progress,
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** A judged attempt on a floor (updates its mastery estimate). */
  attempt(floorId: string, accepted: boolean, young: boolean) {
    set(recordAttempt(progress, floorId, accepted, Date.now(), young));
  },
  /** A clear r when transmitting the codename. */
  bonus(floorId: string) {
    set(recordBonus(progress, floorId));
  },
  revealed(floorId: string, letters: number) {
    set(recordRevealed(progress, floorId, letters));
  },
  missionDone(
    floorId: string,
    result: MissionResult,
    revealed: number,
    letters: number,
  ): { stars: Stars; improved: boolean; previousBest: number | null } {
    const out = recordMission(progress, floorId, result, revealed, letters);
    set(out.progress);
    return { stars: out.stars, improved: out.improved, previousBest: out.previousBest };
  },
  /** Development and tests: start over, or load a given state. */
  reset(next: Progress = {}) {
    set(next);
  },
};

export function useProgress(): Progress {
  return useSyncExternalStore(progressStore.subscribe, progressStore.get);
}
